/** Joint Decisions Value tab lookup helpers (Medicare reimbursement). */

export const VALUE_SETTINGS = [
  { key: 'office', label: 'Office' },
  { key: 'asc', label: 'ASC', title: 'Ambulatory Service Center' },
  { key: 'hopd', label: 'HOPD', title: 'Hospital Outpatient Department' },
  { key: 'inpatient', label: 'Inpatient' },
];

const EPIDURAL_PROCEDURE_KEYS = new Set([
  'cervical_epidural_injection',
  'thoracic_epidural_injection',
  'lumbar_epidural_injection',
]);

const PERMANENT_SCS_PROCEDURE_KEYS = new Set([
  'cervical_scs',
  'thoracic_scs',
  'lumbar_scs',
  'SCS',
]);

const CERVICAL_LAMINOPLASTY_KEYS = new Set([
  'cervical_laminoplasty',
  'LAMINOPLASTY',
]);

export function isEpiduralInjectionProcedure(procedureKey) {
  return EPIDURAL_PROCEDURE_KEYS.has(procedureKey)
    || String(procedureKey || '').includes('epidural_injection');
}

export function isPermanentScsProcedure(procedureKey) {
  return PERMANENT_SCS_PROCEDURE_KEYS.has(procedureKey)
    || /(^|_)scs$/i.test(String(procedureKey || ''));
}

export function isCervicalLaminoplastyProcedure(procedureKey) {
  return CERVICAL_LAMINOPLASTY_KEYS.has(procedureKey);
}

/**
 * CMS setting applicability. Returns an N/A reason or null when the setting is allowed.
 * Office: epidural injection only (CPT 62321 / 62323, POS 11). Permanent SCS is ASC/hospital
 * (CMS LCD L35136). Inpatient: N/A only for epidural. ASC/HOPD: N/A only for cervical
 * laminoplasty (CPT 63050/63051, OPPS status C in 2026).
 */
export function valueSettingNaReason(procedureKey, setting) {
  if (!procedureKey || !setting) return null;
  if (setting === 'office') {
    if (isEpiduralInjectionProcedure(procedureKey)) return null;
    if (isPermanentScsProcedure(procedureKey)) {
      return 'Permanent SCS must be ASC or hospital (CMS LCD L35136).';
    }
    return 'Office is N/A for operating-room surgery. Only epidural injection (CPT 62321 / 62323, POS 11) is payable here.';
  }
  if (setting === 'inpatient') {
    if (isEpiduralInjectionProcedure(procedureKey)) {
      return 'Epidural injection is not an inpatient index case.';
    }
    return null;
  }
  if (setting === 'asc' || setting === 'hopd') {
    if (isCervicalLaminoplastyProcedure(procedureKey)) {
      return 'Cervical laminoplasty (CPT 63050/63051) remains CMS inpatient-only (OPPS status C) in 2026.';
    }
    return null;
  }
  return null;
}

/**
 * Maps value complication keys to spreadsheet risk labels.
 * Optional `group` prevents double-counting one risk probability across variants
 * (first applicable variant in list order receives the probability).
 */
export const VALUE_COMPLICATION_RISK_SPECS = [
  { key: 'myocardial_infarction', riskLabel: 'Myocardial Infarction' },
  { key: 'stroke', riskLabel: 'Stroke' },
  { key: 'death', riskLabel: 'Death' },
  { key: 'pulmonary_embolism', riskLabel: 'Pulmonary Embolism' },
  { key: 'deep_vein_thrombosis', riskLabel: 'Deep Vein Thrombosis' },
  { key: 'deep_surgical_site_infection', riskLabel: 'Surgical Site Infection', group: 'ssi' },
  { key: 'superficial_surgical_site_infection', riskLabel: 'Surgical Site Infection', group: 'ssi' },
  { key: 'neurological_injury_permanent', riskLabel: 'Neurological Injury', group: 'neuro' },
  { key: 'neurological_injury_transient', riskLabel: 'Neurological Injury', group: 'neuro' },
  { key: 'postoperative_bleeding_wound_hematoma', riskLabel: 'Postoperative Bleeding/Wound Hematoma' },
  { key: 'persistent_csf_leak_after_durotomy', riskLabel: 'Intraoperative Incidental Durotomy', group: 'durotomy' },
  { key: 'incidental_durotomy_repaired_immediately', riskLabel: 'Intraoperative Incidental Durotomy', group: 'durotomy' },
  { key: 'vascular_injury', riskLabel: 'Vascular Injury' },
  { key: 'visceral_organ_injury', riskLabel: 'Visceral Organ Injury' },
  { key: 'adjacent_segment_disease_requiring_surgery', riskLabel: 'Adjacent Segment Disease', group: 'asd' },
  { key: 'adjacent_segment_disease', riskLabel: 'Adjacent Segment Disease', group: 'asd' },
  { key: 'non_union_requiring_revision_fusion', riskLabel: 'Non-Union', group: 'non_union' },
  { key: 'non_union_nonsurgical_management', riskLabel: 'Non-Union', group: 'non_union' },
  { key: 'reherniation_requiring_repeat_microdiscectomy', riskLabel: 'Reherniation', group: 'reherniation' },
  { key: 'reherniation_after_microdiscectomy', riskLabel: 'Reherniation', group: 'reherniation' },
  { key: 'abdominal_hernia', riskLabel: 'Abdominal Hernia' },
];

export function parseRiskProbability(raw) {
  if (raw == null || raw === '') return 0;
  const text = String(raw).replace(/%/g, '').trim();
  if (!text || /^n\/?a$/i.test(text) || text === '-') return 0;
  const n = parseFloat(text);
  if (!Number.isFinite(n) || n <= 0) return 0;
  return n / 100;
}

export function formatRiskProbabilityPercent(probability) {
  const n = Number(probability);
  if (!Number.isFinite(n) || n <= 0) return '0%';
  const pct = n * 100;
  if (pct < 0.01) return '~.01%';
  const text = pct.toFixed(2).replace(/\.?0+$/, '').replace(/^0\./, '.');
  return `${text}%`;
}

function finiteOrNull(value) {
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
}

/** CC = minor, MCC = major. Shares are renormalized when both are present. */
export function complicationSeverityMix(row = {}) {
  const minorCost = finiteOrNull(row.minor_central);
  const majorCost = finiteOrNull(row.major_central);
  let minorShare = finiteOrNull(row.minor_share);
  let majorShare = finiteOrNull(row.major_share);
  const hasShares = minorShare != null && majorShare != null && minorShare + majorShare > 0;

  if (hasShares) {
    const total = minorShare + majorShare;
    minorShare /= total;
    majorShare /= total;
  } else if (minorCost != null && majorCost != null) {
    minorShare = 0.5;
    majorShare = 0.5;
  } else if (majorCost != null) {
    minorShare = 0;
    majorShare = 1;
  } else if (minorCost != null) {
    minorShare = 1;
    majorShare = 0;
  } else {
    const central = finiteOrNull(row.central);
    return {
      minorShare: 0,
      majorShare: 1,
      minorCost: 0,
      majorCost: central == null ? 0 : central,
    };
  }

  return {
    minorShare: minorShare || 0,
    majorShare: majorShare || 0,
    minorCost: minorCost == null ? 0 : minorCost,
    majorCost: majorCost == null ? 0 : majorCost,
  };
}

export function expectedComplicationMidpoint(row) {
  const mix = complicationSeverityMix(row);
  return mix.minorShare * mix.minorCost + mix.majorShare * mix.majorCost;
}

export function formatExpectedValueFormula({
  probability,
  minorShare,
  majorShare,
  minorCost,
  majorCost,
}) {
  const event = formatRiskProbabilityPercent(probability);
  const terms = [];
  if ((majorShare || 0) > 0) {
    terms.push(
      `${event} × ${formatRiskProbabilityPercent(majorShare)} major × ${formatUsd(majorCost)}`,
    );
  }
  if ((minorShare || 0) > 0) {
    terms.push(
      `${event} × ${formatRiskProbabilityPercent(minorShare)} minor × ${formatUsd(minorCost)}`,
    );
  }
  if (!terms.length) {
    return `${event} × ${formatUsd(0)}`;
  }
  return terms.join(' + ');
}

/** Positive when prior − after improves a lower-is-better score (pain, NDI/ODI). */
export function outcomePointReduction(prior, after) {
  const p = Number(prior);
  const a = Number(after);
  if (!Number.isFinite(p) || !Number.isFinite(a)) return null;
  return p - a;
}

export function formatPointReduction(reduction) {
  if (reduction == null || !Number.isFinite(reduction)) return 'N/A';
  if (reduction <= 0) return 'No reduction';
  const text = Number.isInteger(reduction)
    ? String(reduction)
    : reduction.toFixed(1).replace(/\.0$/, '');
  return `${text} pt`;
}

function costPerPoint(expectedCost, reduction) {
  if (expectedCost == null || reduction == null || reduction <= 0) return null;
  const cost = Number(expectedCost);
  if (!Number.isFinite(cost)) return null;
  return cost / reduction;
}

export function computeCostPerPointMetrics(expected, outcomes = {}) {
  const painReduction = outcomePointReduction(outcomes.pain_prior, outcomes.pain_after);
  const dysfunctionReduction = outcomePointReduction(
    outcomes.function_prior,
    outcomes.function_after,
  );
  return {
    painReduction,
    dysfunctionReduction,
    costPerPainPoint: costPerPoint(expected.central, painReduction),
    costPerDysfunctionPoint: costPerPoint(expected.central, dysfunctionReduction),
  };
}

function complicationByKey(complications) {
  return new Map((complications || []).map((row) => [row.key, row]));
}

export function computeProbabilisticExpectedValue({
  payload,
  procedureKey,
  levels,
  setting,
  risks = {},
}) {
  const settingNaReason = valueSettingNaReason(procedureKey, setting);
  if (settingNaReason) {
    return {
      payable: false,
      base: null,
      incrementalLow: 0,
      incrementalCentral: 0,
      incrementalHigh: 0,
      low: null,
      central: null,
      high: null,
      naReason: settingNaReason,
      breakdown: [],
    };
  }

  const rate = lookupValueRate(payload, procedureKey, levels, setting);
  const complications = lookupValueComplications(payload, procedureKey, levels, setting);
  const byKey = complicationByKey(complications);
  const base = valueBaseAmount(rate);
  if (base == null) {
    return {
      payable: false,
      base: null,
      incrementalLow: 0,
      incrementalCentral: 0,
      incrementalHigh: 0,
      low: null,
      central: null,
      high: null,
      naReason: rate?.na_reason || 'Not payable in this setting',
      breakdown: [],
    };
  }

  const usedGroups = new Set();
  let incrementalCentral = 0;
  const breakdown = [];

  VALUE_COMPLICATION_RISK_SPECS.forEach((spec) => {
    const row = byKey.get(spec.key);
    if (!row) return;
    if (spec.group && usedGroups.has(spec.group)) return;

    const probability = parseRiskProbability(risks?.[spec.riskLabel]);
    const mix = complicationSeverityMix(row);
    const costCentral = expectedComplicationMidpoint(row);
    const contributionCentral = probability * costCentral;
    const contributionMajor = probability * mix.majorShare * mix.majorCost;
    const contributionMinor = probability * mix.minorShare * mix.minorCost;

    incrementalCentral += contributionCentral;

    breakdown.push({
      key: spec.key,
      label: row.label || spec.key,
      riskLabel: spec.riskLabel,
      probability,
      minorShare: mix.minorShare,
      majorShare: mix.majorShare,
      minorCost: mix.minorCost,
      majorCost: mix.majorCost,
      costCentral,
      contributionMinor,
      contributionMajor,
      contributionCentral,
    });

    if (spec.group) usedGroups.add(spec.group);
  });

  return {
    payable: true,
    base,
    incrementalLow: incrementalCentral,
    incrementalCentral,
    incrementalHigh: incrementalCentral,
    low: base + incrementalCentral,
    central: base + incrementalCentral,
    high: base + incrementalCentral,
    naReason: null,
    breakdown,
  };
}

export function formatUsd(value) {
  if (value == null || value === '') return 'N/A';
  const n = Number(value);
  if (!Number.isFinite(n)) return 'N/A';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0,
  }).format(n);
}

export function formatUsdAxisTick(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return '';
  if (n === 0) return '$0';
  if (Math.abs(n) >= 1000) {
    const thousands = n / 1000;
    const text = Number.isInteger(thousands) ? String(thousands) : thousands.toFixed(1).replace(/\.0$/, '');
    return `$${text}k`;
  }
  return formatUsd(n);
}

export function lookupValueRate(payload, procedureKey, levels, setting) {
  if (!payload?.rates || !procedureKey || !setting) return null;
  return payload.rates[procedureKey]?.[String(levels)]?.[setting] || null;
}

export function lookupValueComplications(payload, procedureKey, levels, setting) {
  if (!payload?.complications || !procedureKey || !setting) return [];
  const rows = payload.complications[procedureKey]?.[String(levels)]?.[setting];
  return Array.isArray(rows) ? rows : [];
}

export function valueBaseAmount(rate) {
  if (!rate || rate.payable === false) return null;
  const amount = Number(rate.normal);
  return Number.isFinite(amount) ? amount : null;
}
